import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useExtensionEnrollment
 *
 * Auto-detects if the EAloom Container Monitor Chrome extension is installed,
 * and if so, automatically enrolls it using the current user's Web session.
 *
 * Flow:
 *   1. On mount, ping the extension via chrome.runtime.sendMessage
 *   2. If extension responds → check if already enrolled (has token)
 *   3. If not enrolled → send enroll message with current session token
 *   4. Extension uses the token to call POST /api/extension/enroll
 *   5. Extension gets its own long-lived extension_token and connects via WS
 *
 * See docs/CONTAINER_MONITOR_DESIGN.md §8.5.
 */

// Extension ID — in production this comes from env var
// For development (Load unpacked), use the ID from chrome://extensions
const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';

// Anchor the strip to the END of the URL. A plain string .replace('/api', '')
// removes the FIRST match, and in production VITE_API_URL is
// https://api.ealoom.com/api — whose first "/api" is inside "//api.ealoom.com".
// That produced "https:/.ealoom.com/api", so every enroll POST from the extension
// hit a non-existent host and silently failed. See docs/EXTENSION_WEB_HANDSHAKE_DIAGNOSIS_2026-08-13.md.
const BACKEND_URL = import.meta.env.VITE_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:4000';

interface ExtensionState {
  /** Whether we've checked for the extension */
  checked: boolean;
  /** Whether the extension is installed */
  installed: boolean;
  /**
   * Whether the extension is enrolled to the CURRENTLY LOGGED-IN user's tenant.
   * An extension bound to a different tenant (leftover from the previous user
   * on the same Chrome profile) counts as NOT enrolled — the Container Monitor
   * badge must stay red until auto-rebind finishes, otherwise we'd show a
   * green footer while the backend correctly reports offline for this tenant.
   */
  enrolled: boolean;
  /** Extension version (if installed) */
  version: string | null;
  /** Connection state reported by extension */
  connectionState: 'disconnected' | 'connecting' | 'authenticated' | null;
  /** Error message if enrollment failed */
  error: string | null;
}

function getCurrentUserCompanyId(): number | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.company_id === 'number' ? parsed.company_id : null;
  } catch {
    return null;
  }
}

interface UseExtensionEnrollmentOptions {
  /**
   * Whether this instance may enrol/re-bind, or is only observing.
   *
   * ExtensionBridge owns enrolment app-wide. Other consumers (the Container
   * Monitor modal footer) just want to display state, and letting them enrol
   * too would run two independent retry ladders against the same extension.
   */
  autoEnroll?: boolean;
}

export function useExtensionEnrollment({ autoEnroll = true }: UseExtensionEnrollmentOptions = {}) {
  const [state, setState] = useState<ExtensionState>({
    checked: false,
    installed: false,
    enrolled: false,
    version: null,
    connectionState: null,
    error: null,
  });

  // A single failed enrollment used to poison the whole page load: attemptedRef
  // was a one-shot boolean, so one flaky request meant the badge stayed red
  // until a full reload. Bounded retries with backoff instead — enough to ride
  // out a hiccup, few enough that a genuinely broken setup doesn't hammer the
  // backend on every visibility change.
  const attemptsRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_ENROLL_ATTEMPTS = 3;

  // checkExtension schedules a retry of itself; going through a ref keeps it
  // out of its own useCallback dependency list.
  const checkExtensionRef = useRef<(() => Promise<void>) | null>(null);

  const checkExtension = useCallback(async () => {
    // Only available in Chrome with externally_connectable
    if (!window.chrome?.runtime?.sendMessage) {
      setState(s => ({ ...s, checked: true, installed: false }));
      return;
    }

    try {
      // Ping the extension
      const response = await new Promise<any>((resolve) => {
        chrome.runtime.sendMessage(EXT_ID, { type: 'ping' }, (resp) => {
          if (chrome.runtime.lastError) {
            resolve(null); // Extension not installed or not responding
          } else {
            resolve(resp);
          }
        });
      });

      if (!response || !response.ok) {
        setState(s => ({ ...s, checked: true, installed: false }));
        return;
      }

      // "Enrolled" semantics:
      //   - Strict match (new extension exposes tenantId in ping): require
      //     response.tenantId === currentCompanyId.
      //   - Old extension without tenantId (user hasn't reloaded the Chrome
      //     extension yet after we shipped the tenantId field): fall back to
      //     the old behavior of trusting state==='authenticated'. Otherwise
      //     every page load during the rollout window would show amber
      //     "Not Enrolled" on perfectly healthy sessions.
      //   - The header badge (driven by backend's tenantIndex, not this hook)
      //     is the authoritative source of truth for "is this tenant actually
      //     online", so a lenient footer here doesn't create false-positives.
      const currentCompanyId = getCurrentUserCompanyId();
      const boundToCurrentTenant =
        response.state === 'authenticated' &&
        (
          typeof response.tenantId !== 'number' ||
          currentCompanyId === null ||
          response.tenantId === currentCompanyId
        );

      setState(s => ({
        ...s,
        checked: true,
        installed: true,
        version: response.version,
        connectionState: response.state,
        enrolled: boundToCurrentTenant,
      }));

      // Trigger auto-rebind only when we have an ACTIVE signal. Ambiguous
      // cases (old extension) default to "leave alone" — otherwise every
      // refresh would kick a healthy WS through an unnecessary reconnect.
      const needsRebind =
        response.state !== 'authenticated' ||
        (
          typeof response.tenantId === 'number' &&
          currentCompanyId !== null &&
          response.tenantId !== currentCompanyId
        );

      if (needsRebind && autoEnroll && attemptsRef.current < MAX_ENROLL_ATTEMPTS) {
        attemptsRef.current += 1;
        const ok = await enrollExtension();
        // Schedule one retry per remaining attempt: 2s, then 6s. The
        // extension itself de-dupes concurrent enrolls, so a retry racing
        // another tab is harmless.
        if (!ok && attemptsRef.current < MAX_ENROLL_ATTEMPTS && !retryTimerRef.current) {
          const delay = 2000 * Math.pow(3, attemptsRef.current - 1);
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            checkExtensionRef.current?.();
          }, delay);
        }
      }
    } catch (err) {
      setState(s => ({ ...s, checked: true, installed: false, error: (err as Error).message }));
    }
  }, [autoEnroll]);

  /** @returns true when the extension reported a successful enrollment. */
  const enrollExtension = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) {
      setState(s => ({ ...s, error: 'No session token. Please log in first.' }));
      return false;
    }

    try {
      const response = await new Promise<any>((resolve) => {
        chrome.runtime.sendMessage(EXT_ID, {
          type: 'enroll',
          session_token: token,
          backend_url: BACKEND_URL,
          // The extension's popup needs to know where the Web app lives so its
          // "Enroll via Web App" button can bring the user back here. It cannot
          // infer it from backend_url — in prod that's a different host.
          web_url: window.location.origin,
        }, (resp) => {
          if (chrome.runtime.lastError) {
            resolve({ ok: false, error: chrome.runtime.lastError.message });
          } else {
            resolve(resp);
          }
        });
      });

      if (response?.ok) {
        setState(s => ({
          ...s,
          enrolled: true,
          connectionState: 'authenticated',
          error: null,
        }));
        return true;
      }
      setState(s => ({ ...s, error: response?.error || 'Enrollment failed' }));
      return false;
    } catch (err) {
      setState(s => ({ ...s, error: (err as Error).message }));
      return false;
    }
  }, []);

  checkExtensionRef.current = checkExtension;

  // Check on mount + when tab becomes visible (user returns from Chrome Web Store install)
  useEffect(() => {
    checkExtension();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkExtension();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [checkExtension]);

  return {
    ...state,
    checkExtension,
    enrollExtension,
  };
}
